import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Building2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import DateInput from "@/components/DateInput";
import MergeDialog from "@/components/MergeDialog";
import ExcelButton from "@/components/ExcelButton";
import { fmtTL, fmtDate } from "@/lib/utils";

const EMPTY = { name: "", code: "", location: "", client_name: "", start_date: "", end_date: "", budget: 0, status: "aktif", description: "" };
const STATUS_LABEL = { aktif: "Aktif", tamamlandi: "Tamamlandı", durduruldu: "Durduruldu" };

export default function Projects() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/projects").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.code) { toast.error("Proje adı ve kodu zorunlu"); return; }
    try {
      await api.post("/projects", { ...form, budget: Number(form.budget) || 0 });
      toast.success("Proje oluşturuldu");
      setOpen(false); setForm(EMPTY); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Projeyi silmek istiyor musunuz?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Silindi"); load();
  };

  const statusColor = { aktif: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", tamamlandi: "bg-blue-500/15 text-blue-700 dark:text-blue-400", durduruldu: "bg-rose-500/15 text-rose-700 dark:text-rose-400" };

  return (
    <div data-testid="projects-page">
      <PageHeader title="Projeler ve Şantiyeler" subtitle="Tüm projelerinizi ve şantiyelerinizi yönetin.">
        <MergeDialog type="project" entities={rows.filter(r => r.source === "excel")} onMerged={load} />
        <ExcelButton testid="projects-excel-button" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-project-button" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold">
              <Plus className="w-4 h-4 mr-1" /> Yeni Proje
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Yeni Proje</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Proje Kodu *</Label><Input data-testid="input-code" value={form.code} onChange={e => setForm({...form, code: e.target.value})} /></div>
              <div><Label>Proje Adı *</Label><Input data-testid="input-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
              <div><Label>Lokasyon</Label><Input data-testid="input-location" value={form.location} onChange={e => setForm({...form, location: e.target.value})} /></div>
              <div><Label>İşveren</Label><Input data-testid="input-client" value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} /></div>
              <div><Label>Başlangıç</Label><DateInput data-testid="input-start" value={form.start_date || ""} onChange={e => setForm({...form, start_date: e.target.value})} /></div>
              <div><Label>Bitiş</Label><DateInput data-testid="input-end" value={form.end_date || ""} onChange={e => setForm({...form, end_date: e.target.value})} /></div>
              <div><Label>Bütçe (TL)</Label><Input type="number" data-testid="input-budget" value={form.budget} onChange={e => setForm({...form, budget: e.target.value})} /></div>
              <div>
                <Label>Durum</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger data-testid="input-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aktif">Aktif</SelectItem>
                    <SelectItem value="tamamlandi">Tamamlandı</SelectItem>
                    <SelectItem value="durduruldu">Durduruldu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Açıklama</Label><Textarea data-testid="input-desc" value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
              <Button className="bg-amber-500 hover:bg-amber-600 text-slate-950" onClick={save} data-testid="save-project-button">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {rows.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead>Kod</TableHead>
                <TableHead>Proje Adı</TableHead>
                <TableHead>Lokasyon</TableHead>
                <TableHead>İşveren</TableHead>
                <TableHead className="text-right">Firma</TableHead>
                <TableHead className="text-right">Hakediş Toplamı</TableHead>
                <TableHead className="text-right">Bütçe</TableHead>
                <TableHead>Dönem</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`project-row-${r.code}`}>
                  <TableCell className="font-mono-nums font-semibold">{r.code}</TableCell>
                  <TableCell className="font-medium">{r.name}
                    {r.merged_names?.length > 1 && <div className="text-[11px] text-muted-foreground font-normal">{r.merged_names.join(" + ")}</div>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{r.location || "-"}</TableCell>
                  <TableCell className="text-muted-foreground">{r.client_name || "-"}</TableCell>
                  <TableCell className="text-right font-mono-nums">{r.firm_count || 0}</TableCell>
                  <TableCell className="text-right font-mono-nums font-semibold text-amber-700 dark:text-amber-400">{fmtTL(r.total_amount)}</TableCell>
                  <TableCell className="text-right font-mono-nums">{fmtTL(r.budget)}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{fmtDate(r.start_date)} — {fmtDate(r.end_date)}</TableCell>
                  <TableCell><Badge className={statusColor[r.status] || ""}>{STATUS_LABEL[r.status] || r.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`delete-project-${r.code}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border-2 border-dashed border-border p-12 text-center">
      <Building2 className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
      <div className="font-semibold mb-1">Henüz proje yok</div>
      <p className="text-sm text-muted-foreground">İlk projenizi oluşturmak için &quot;Yeni Proje&quot; butonuna tıklayın.</p>
    </div>
  );
}
